/**
 * PATH: packages/ui/src/components/dashboard-layout/useScrollToTopOnRouteChange.ts
 * FILE_ID: UI-LAYOUT-HOOK-SCROLLTOP-001
 * PURPOSE: Reset the DashboardLayout content scroll container back to the top
 *          whenever the route (pathname) changes.
 * WHY:     The shared `<main>` scroll container (`overflow-y-auto`) is part of
 *          the PERSISTENT app shell — it never remounts on navigation. React
 *          Router swaps the routed children in place but leaves the container's
 *          scrollTop untouched, so clicking a sidebar / menu item used to land
 *          the operator mid-page at the PREVIOUS view's scroll offset
 *          (operator report 2026-06-18: "When I am clicking a menu item it is
 *          not auto going to the top"). Standard SPA behaviour is top-on-nav.
 * FLOW:
 * ┌──────────────────┐   ┌───────────────────────┐   ┌──────────────────────┐
 * │ pathname changed │ → │ skip first run (mount) │ → │ container.scrollTo(0) │
 * └──────────────────┘   └───────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 * - react: useEffect / useRef (effect + first-run guard).
 * RELATED:
 * - ../DashboardLayout.tsx: owns the `<main>` ref and feeds it the live pathname.
 */

import { useEffect, useRef, type RefObject } from 'react'

/**
 * scrollContainerToTop
 * --------------------
 * Pure I->O side effect: move a scroll container to its top-left origin.
 * No-op when the element is absent (ref not yet attached). Uses `scrollTo`
 * when available (covers jsdom + every evergreen browser) and falls back to
 * direct property assignment so the behaviour is testable without a real
 * layout engine.
 */
export function scrollContainerToTop(el: HTMLElement | null | undefined): void {
  if (!el) return
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ top: 0, left: 0 })
    return
  }
  el.scrollTop = 0
  el.scrollLeft = 0
}

/**
 * useScrollToTopOnRouteChange
 * ---------------------------
 * Scroll `containerRef` to the top each time `pathname` changes. The FIRST
 * run (initial mount) is intentionally skipped: a fresh page load already
 * starts at the top, and skipping it avoids yanking any deep-linked / hash
 * target on first paint. Keyed ONLY on the pathname (not search/hash) so
 * in-page filtering (query-param changes) does not reset the operator's
 * scroll position — only true navigations do.
 */
export function useScrollToTopOnRouteChange(
  containerRef: RefObject<HTMLElement | null>,
  pathname: string,
): void {
  const isFirstRun = useRef(true)
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    scrollContainerToTop(containerRef.current)
  }, [pathname, containerRef])
}
